import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import { FormattedMessage } from 'react-intl';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { DialogTitle, FormHelperText } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { nanoid } from 'nanoid';
import {
  FieldWrapperVertical,
  SnackbarApiErrorMessage,
  useDataProvider,
  useDialog,
  useLocalization,
  LoadingEvent,
  useSnackbar,
} from '@ui-framework/index';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { CLAIM_APPROVE_CODE_OTHER } from '@constants/app';
import ClaimsApproveReasons from '../../../ClaimsApproveReasons';

function ApproveOverrideClaimModal({
  open,
  onClose,
  claimId,
  serviceJobId,
  formRef,
  preProcessClaimRequest,
  setOpenModal,
  serviceJobStatus,
}) {
  const { formatMessage } = useLocalization();
  const { confirmationRef } = useDialog();

  const validationschema = yup.object().shape({
    approveReasonCode: yup.object().required(
      formatMessage({
        id: 'APPROVE_REASON_IS_REQUIRED',
        defaultMessage: 'Approve reason is required',
      }),
    ),
    approveReasonOtherText: yup.string().when('approveReasonCode', {
      is: val => val?.value === CLAIM_APPROVE_CODE_OTHER,
      then: schema =>
        schema.required(
          formatMessage({
            id: 'PLEASE_SPECIFY_A_REASON',
            defaultMessage: 'Please specify a reason',
          }),
        ),
      otherwise: schema => schema.notRequired(),
    }),
  });

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, put } = useDataProvider(provider);

  const snackbar = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [canApprove, setCanApprove] = useState(false);

  const methods = useForm({
    resolver: yupResolver(validationschema),
  });
  const { handleSubmit, control } = methods;

  const buttons = [
    {
      children: formatMessage({
        id: 'APPROVE_OVERRIDE_CLAIM',
        defaultMessage: 'Approve Override Claim',
      }),
      type: 'submit',
      variant: 'contained',
      color: 'primary',
    },
    {
      children: formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' }),
      onClick: () => setOpenModal(false),
      variant: 'contained',
      color: 'secondary',
    },
  ];

  const preProcessRequestData = data => {
    const claimDialogFormData = preProcessClaimRequest(
      formRef.current.getValues(),
      true,
    );

    const processedData = {};
    const { approveReasonCode, approveReasonOtherText } = data;
    processedData.approveOverrideCode = approveReasonCode?.value;
    processedData.approveOverrideText =
      approveReasonCode.value === CLAIM_APPROVE_CODE_OTHER
        ? approveReasonOtherText
        : approveReasonCode?.label;
    if (claimDialogFormData?.claimAmounts) {
      processedData.claimAmounts = claimDialogFormData?.claimAmounts;
    }

    processedData.totalPartsApproved = claimDialogFormData?.totalPartsApproved;
    processedData.recordVersion = claimDialogFormData?.recordVersion;
    return processedData;
  };

  const submitApprove = async data => {
    setLoading(true);
    const body = preProcessRequestData(data);
    const response = await put(
      `${getApiUrl()}/sxcl/v1/service-jobs/${serviceJobId}/claims/${claimId}/approve-override`,
      body,
    );
    setLoading(false);
    if (response.ok) {
      snackbar.showMessage({
        type: 'success',
        data: (
          <FormattedMessage
            id="CLAIM_APPROVE_OVERRIDE_SUCCESSFUL"
            defaultMessage="Claim approve override successful"
          />
        ),
      });
      onClose(nanoid());
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
    }
  };

  const onFormSubmit = data => {
    submitApprove(data);
  };

  const initialize = async () => {
    if (serviceJobStatus === 'CLAIM_IN_PROCESS') {
      confirmationRef.current.init({
        message:
          'There is another claim in process for this Job. Do you want to proceed?',
        positiveButtonText: 'Yes',
        negativeButtonText: 'No',
        width: 360,
        title: 'Claim In Process Warning',
        onChange: isYes => {
          if (isYes) {
            confirmationRef.current.setOpen(false);
            setCanApprove(true);
          } else {
            confirmationRef.current.setOpen(false);
            onClose(nanoid());
          }
        },
      });
      confirmationRef.current.setOpen(true);
    } else {
      setCanApprove(true);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  if (!canApprove) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 400,
        bgcolor: 'background.paper',
      }}>
      <Dialog
        id="approveDialog"
        sx={{ '& .MuiDialog-paper': { width: '80%' } }}
        open={open}>
        {!loading && (
          <DialogTitle style={{ padding: '8px 20px 30px', fontSize: '18px' }}>
            <FormattedMessage
              id="APPROVE_OVERRIDE_CLAIM"
              defaultMessage="Approve Override Claim"
            />
          </DialogTitle>
        )}
        {loading ? (
          <DialogContent>
            <LoadingEvent>
              <FormattedMessage
                id="PROCESSING_APPROVE_OVERRIDE_CLAIM"
                defaultMessage="Processing Approve Override Claim..."
              />
            </LoadingEvent>
          </DialogContent>
        ) : (
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onFormSubmit)}>
              <DialogContent>
                <Grid2 container spacing={2} flexDirection="column">
                  <Grid2>
                    <Controller
                      name="approveReasonCode"
                      control={control}
                      render={({ field, fieldState: { error: fsError } }) => {
                        return (
                          <FieldWrapperVertical
                            field={{
                              name: field?.name,
                              label: {
                                localeId: 'PLEASE_ADD_APPROVE_REASON',
                                defaultMessage: 'Please add approve reason',
                              },
                            }}>
                            <ClaimsApproveReasons
                              control={control}
                              {...field}
                              field={{
                                dependencies: {
                                  other: {
                                    name: 'approveReasonOtherText',
                                    placeholder: {
                                      id: 'PLEASE_ADD_APPROVE_REASON',
                                      defaultMessage:
                                        'Please add approve reason',
                                    },
                                  },
                                },
                              }}
                            />
                            {fsError?.message ? (
                              <FormHelperText
                                sx={{ typography: 'body2' }}
                                error={Boolean(fsError?.message)}>
                                {fsError?.message}
                              </FormHelperText>
                            ) : null}
                          </FieldWrapperVertical>
                        );
                      }}
                    />
                  </Grid2>
                </Grid2>
              </DialogContent>
              {!loading ? (
                <DialogActions
                  display="flex"
                  sx={{ justifyContent: 'center', my: 2 }}>
                  {buttons.map(btn => {
                    const { children, ...rest } = btn;
                    return (
                      <Button key={children} disabled={loading} {...rest}>
                        {children}
                      </Button>
                    );
                  })}
                </DialogActions>
              ) : null}
            </form>
          </FormProvider>
        )}
      </Dialog>
    </Box>
  );
}

export default ApproveOverrideClaimModal;
